import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';

const AnimatedTitle = ({ animate = true }) => {
  const styles = `
    .title-text {
      font-family: 'Cormorant Garamond', serif;
      font-weight: 500;
      fill: #f8d9d0;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
    }
    
    .jp-text {
      font-family: 'Noto Sans JP', sans-serif;
      font-weight: 400;
      fill: #f8d9d0;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
    }

    .title-container {
      pointer-events: none;
    }
  `;

  const mainRef = useRef(null);

  useEffect(() => {
    if (!animate || !mainRef.current) return;

    const mainTitle = mainRef.current.querySelector('.main-title');
    const subTitle = mainRef.current.querySelector('.sub-title');
    const tagline = mainRef.current.querySelector('.tagline');
    const jpText = mainRef.current.querySelectorAll('.jp-text');

    // Set initial states
    gsap.set([mainTitle, subTitle, tagline, jpText], {
      opacity: 0
    });

    gsap.set(mainTitle, { y: -20 });
    gsap.set(subTitle, { y: 20 });
    gsap.set(tagline, { y: 20 });
    gsap.set(jpText, { y: -10 });

    // Create timeline for sequential animation
    const tl = gsap.timeline();

    // Main title animation
    tl.to(mainTitle, {
      y: 0,
      opacity: 1,
      duration: 1.5,
      ease: 'power2.out',
    });

    // Subtitle animation
    tl.to(subTitle, {
      y: 0,
      opacity: 1,
      duration: 1.2,
      ease: 'power2.out',
    }, '-=0.8');

    // Tagline animation
    tl.to(tagline, {
      y: 0,
      opacity: 1,
      duration: 1,
      ease: 'power2.out',
    }, '-=0.6');

    // Japanese text animation
    jpText.forEach((el, index) => {
      tl.to(el, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out',
      }, `-=${0.4}`);
    });

    // Add subtle shimmer animations
    const addShimmer = () => {
      // Rose gold shimmer for main title
      gsap.to(mainTitle, {
        filter: 'drop-shadow(0 0 8px rgba(248,217,208,0.8))',
        duration: 2,
        repeat: -1,
        yoyo: true,
      });

      // Soft pink shimmer for subtitle
      gsap.to(subTitle, {
        filter: 'drop-shadow(0 0 6px rgba(255,192,203,0.6))',
        duration: 2.3,
        repeat: -1,
        yoyo: true,
        delay: 0.3
      });

      // Gentle shimmer for tagline
      gsap.to(tagline, {
        filter: 'drop-shadow(0 0 5px rgba(248,217,208,0.5))',
        duration: 2.6,
        repeat: -1,
        yoyo: true,
        delay: 0.6
      });

      // Subtle glow for Japanese text
      jpText.forEach((el, index) => {
        gsap.to(el, {
          filter: 'drop-shadow(0 0 4px rgba(248,217,208,0.4))',
          duration: 2,
          repeat: -1,
          yoyo: true,
          delay: 0.8 + (index * 0.2)
        });
      });
    };

    // Start shimmer animations after initial animations
    tl.call(addShimmer);
  }, [animate]);

  return (
    <div className="w-[400px] h-[200px] title-container">
      <style>{styles}</style>
      <svg ref={mainRef} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 200" className="w-full h-full">
        <defs>
          <style type="text/css">
            @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500&family=Noto+Sans+JP:wght@400&display=swap');
          </style>
        </defs>

        <g transform="translate(200,100)" textAnchor="middle">
          <text
            className="title-text animate-el main-title"
            x="0"
            y="-30"
            fontSize="48"
          >
            Marry Me
          </text>

          <text
            className="title-text animate-el sub-title"
            x="0"
            y="10"
            fontSize="32"
          >
            Instead?
          </text>

          <text
            className="title-text animate-el tagline"
            x="0"
            y="40"
            fontSize="20"
          >
            A Tale of Love & Choice
          </text>

          {[
            { text: '結', x: -45 },
            { text: '婚', x: -15 },
            { text: '物', x: 15 },
            { text: '語', x: 45 }
          ].map((item, index) => (
            <text
              key={index}
              className="jp-text animate-el"
              x={item.x}
              y="65"
              fontSize="15"
            >
              {item.text}
            </text>
          ))}
        </g>
      </svg>
    </div>
  );
};

export default AnimatedTitle;
